import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Package, Truck, AlertCircle, CheckCircle, User, Clock, Hash, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtDT = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const STATUS_COLOR = {
  STORED: 'bg-green-100 text-green-800',
  ALLOCATED: 'bg-amber-100 text-amber-800',
  PICKED_FOR_COMBO: 'bg-indigo-100 text-indigo-800',
  CONSUMED: 'bg-gray-200 text-gray-700',
  COMPLETED: 'bg-green-100 text-green-800',
  ASSEMBLY_IN_PROGRESS: 'bg-sky-100 text-sky-800',
  PENDING_COMPONENTS: 'bg-amber-100 text-amber-800',
  CANCELLED: 'bg-rose-100 text-rose-800',
};

const ComboBundleTrace = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inputRef = useRef(null);
  const [bundleCode, setBundleCode] = useState(params.get('bundle_code') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runTrace = async (code) => {
    const q = (code ?? bundleCode).trim();
    if (!q) { toast.error('Enter a bundle code'); return; }
    setLoading(true);
    try {
      const r = await apiClient.get('/combo/bundle-trace', { params: { bundle_code: q } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trace failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter') runTrace(); };

  const inv = data?.inventory;
  const picks = data?.pick_events || [];
  const sessions = data?.assembly_sessions || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="combo-bundle-trace-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/wms/fg')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Combo Bundle Trace</h1>
          <p className="text-xs text-gray-500">Full tracking of a bundle across combo picking + assembly</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  ref={inputRef}
                  value={bundleCode}
                  onChange={(e) => setBundleCode(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Enter or scan bundle code, e.g. BUN021221/25-26"
                  autoFocus
                  className="pl-8 text-base font-mono"
                  data-testid="bundle-trace-input"
                />
              </div>
              <Button onClick={() => runTrace()} disabled={loading} data-testid="bundle-trace-btn">
                {loading ? 'Tracing...' : 'Trace'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {!data && !loading && (
          <Card><CardContent className="py-10 text-center text-gray-500 text-sm">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Enter a bundle code above to see its full combo history.
          </CardContent></Card>
        )}

        {data && (
          <>
            {/* Inventory summary */}
            <Card className="border-l-4 border-l-indigo-500" data-testid="bundle-inventory-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-indigo-600" />
                  Inventory
                  {inv?.status && (
                    <Badge className={`${STATUS_COLOR[inv.status] || 'bg-gray-100'} border-0 text-[10px]`}>{inv.status}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                  <div className="font-mono font-bold text-base">{inv?.bundle_code}</div>
                  <div className="text-gray-600"><span className="text-gray-400">SKU:</span> {inv?.sku_code || '-'}</div>
                  {inv?.design_name && <div className="text-gray-700 font-medium">{inv.design_name}</div>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  {inv?.shade && <span><span className="text-gray-400">Shade:</span> {inv.shade}</span>}
                  {inv?.size && <span><span className="text-gray-400">Size:</span> {inv.size}</span>}
                  {inv?.sort_no && <span><span className="text-gray-400">Sort:</span> {inv.sort_no}</span>}
                  {inv?.bin_location && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-indigo-500" /> {inv.bin_location}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                  <div className="rounded-md bg-gray-50 border px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Original Pieces</p>
                    <p className="text-lg font-bold" data-testid="inv-original-count">{inv?.original_usn_count ?? '-'}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 border px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Usable USN Codes</p>
                    <p className="text-lg font-bold">{inv?.usn_codes_count ?? '-'}</p>
                  </div>
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700">Consumed by Sessions</p>
                    <p className="text-lg font-bold text-amber-800" data-testid="inv-consumed">{inv?.total_consumed_by_sessions ?? 0}</p>
                  </div>
                  <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-green-700">Live Balance</p>
                    <p className="text-lg font-bold text-green-800" data-testid="inv-balance">{inv?.live_balance ?? 0}</p>
                  </div>
                </div>
                {inv?.last_consumed_at && (
                  <p className="text-[11px] text-gray-500 pt-1">Last consumed at {fmtDT(inv.last_consumed_at)}</p>
                )}
              </CardContent>
            </Card>

            {/* Pick events */}
            <Card data-testid="pick-events-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="w-4 h-4 text-sky-600" />
                  Pick Events
                  <Badge variant="secondary" className="text-[10px]">{picks.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {picks.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No combo pick slips referenced this bundle.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-600 bg-gray-50">
                          <th className="px-2 py-1.5 font-medium">Slip No</th>
                          <th className="px-2 py-1.5 font-medium">Session</th>
                          <th className="px-2 py-1.5 font-medium">Picker</th>
                          <th className="px-2 py-1.5 font-medium">Hand-over (PP)</th>
                          <th className="px-2 py-1.5 font-medium text-right">Required</th>
                          <th className="px-2 py-1.5 font-medium">Outcome</th>
                          <th className="px-2 py-1.5 font-medium">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picks.map((e) => (
                          <tr key={e.slip_no} className="border-b last:border-0 hover:bg-gray-50" data-testid={`pick-event-row-${e.slip_no}`}>
                            <td className="px-2 py-1.5 font-mono">{e.slip_no}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{e.session_id || '-'}</td>
                            <td className="px-2 py-1.5">
                              <div>{e.picker_id || '-'}</div>
                              {e.picker_name && <div className="text-[10px] text-gray-500">{e.picker_name}</div>}
                            </td>
                            <td className="px-2 py-1.5">
                              {e.handover_to_id ? (
                                <div>
                                  <div className="inline-flex items-center gap-1"><User className="w-3 h-3 text-gray-400" />{e.handover_to_id}</div>
                                  {e.handover_to_name && <div className="text-[10px] text-gray-500">{e.handover_to_name}</div>}
                                  {e.handed_over_at && <div className="text-[10px] text-gray-400">{fmtDT(e.handed_over_at)}</div>}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-right font-semibold">{e.required_qty || '-'}</td>
                            <td className="px-2 py-1.5">
                              {e.picked && <Badge className="bg-green-100 text-green-800 border-0 text-[10px]">Picked</Badge>}
                              {e.skipped && (
                                <div>
                                  <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Skipped{e.skip_reason ? ` · ${e.skip_reason}` : ''}</Badge>
                                  {e.skipped_by && <div className="text-[10px] text-gray-500 mt-0.5">by {e.skipped_by}</div>}
                                </div>
                              )}
                              {!e.picked && !e.skipped && <Badge className="bg-gray-100 text-gray-700 border-0 text-[10px]">Pending</Badge>}
                            </td>
                            <td className="px-2 py-1.5 text-[10px] text-gray-600">
                              {e.picked_at && <div>Picked {fmtDT(e.picked_at)}</div>}
                              {e.skipped_at && <div>Skipped {fmtDT(e.skipped_at)}</div>}
                              {!e.picked_at && !e.skipped_at && e.triggered_at && <div>Triggered {fmtDT(e.triggered_at)}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assembly sessions */}
            <Card data-testid="assembly-sessions-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Hash className="w-4 h-4 text-fuchsia-600" />
                  Assembly Sessions
                  <Badge variant="secondary" className="text-[10px]">{sessions.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {sessions.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No assembly sessions have used or are scheduled to use this bundle.</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((s) => (
                      <div key={s.session_id} className="border rounded-lg p-3 hover:bg-gray-50" data-testid={`session-row-${s.session_id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-xs font-semibold">{s.session_id}</span>
                              <Badge className={`${STATUS_COLOR[s.status] || 'bg-gray-100'} border-0 text-[10px]`}>{s.status}</Badge>
                              {s.partial_completion && (
                                <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px]">
                                  Partial ({s.assembled_quantity}/{s.original_target || s.target_quantity})
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium mt-0.5">{s.combo_name || s.combo_barcode}</p>
                            <p className="text-[11px] text-gray-500">
                              {[s.design_name, s.shade, s.size].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">Attributed</p>
                              <p className="text-lg font-bold text-amber-700" data-testid={`session-attributed-${s.session_id}`}>{s.attributed_pieces ?? 0}</p>
                              <p className="text-[10px] text-gray-500">pcs from this bundle</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">Assembled</p>
                              <p className="text-lg font-bold">{s.assembled_quantity ?? 0}<span className="text-gray-400 text-sm">/{s.target_quantity ?? '-'}</span></p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                          <div className={`rounded border px-2 py-1.5 ${s.leftover_declared ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-center gap-1 font-medium">
                              {s.leftover_declared ? <CheckCircle className="w-3 h-3 text-green-600" /> : <AlertCircle className="w-3 h-3 text-amber-600" />}
                              Leftover declaration
                            </div>
                            {s.leftover_declared ? (
                              <div className="mt-0.5 text-gray-700">
                                by <span className="font-medium">{s.leftover_declared_by || '—'}</span>
                                {s.leftover_declared_at && <span className="text-gray-500"> · {fmtDT(s.leftover_declared_at)}</span>}
                                {typeof s.bundle_leftover_qty === 'number' && (
                                  <div className="text-gray-700 mt-0.5">
                                    This bundle leftover: <span className="font-semibold">{s.bundle_leftover_qty}</span> pcs
                                    {s.bundle_leftover_declared_by ? ` (recorded by ${s.bundle_leftover_declared_by})` : ''}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="mt-0.5 text-amber-700">Not yet declared.</div>
                            )}
                          </div>
                          <div className={`rounded border px-2 py-1.5 ${s.excess_returned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-center gap-1 font-medium">
                              {s.excess_returned ? <CheckCircle className="w-3 h-3 text-green-600" /> : <Clock className="w-3 h-3 text-gray-500" />}
                              Excess returned to SFG
                            </div>
                            {s.excess_returned ? (
                              <div className="mt-0.5 text-gray-700">
                                by <span className="font-medium">{s.excess_returned_by || '—'}</span>
                                {s.excess_returned_at && <span className="text-gray-500"> · {fmtDT(s.excess_returned_at)}</span>}
                              </div>
                            ) : (
                              <div className="mt-0.5 text-gray-600">Pending or not applicable.</div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                          {s.created_at && <span>Created {fmtDT(s.created_at)}</span>}
                          {s.completed_at && <span>Completed {fmtDT(s.completed_at)}</span>}
                          {s.assigned_to && <span>Assigned {s.assigned_to}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ComboBundleTrace;
